import { LocationIncident, UnifiedAlarmGroup } from '@hakimo-ui/hakimo/types';
import { useExportCsv } from '@hakimo-ui/hakimo/util';
import { useState } from 'react';
import { ProductType } from '../state';
import { getFormattedExportData, getScanFormattedExportData } from './utils';

interface Params {
  productType: ProductType;
  allIncidents: LocationIncident[];
  allScanIncidents?: UnifiedAlarmGroup[];
}

/**
 * CSV export for whichever incidents table is on screen, with the in-flight
 * flag the toolbar button needs.
 *
 * Lifted verbatim from the toolbar, quirks included, so this stays a move
 * rather than a behaviour change:
 *  - `handleExport` throws. Nothing catches it: React error boundaries do not
 *    see throws from event handlers, so an empty-table export surfaces as an
 *    unhandled rejection (the dev overlay in development, nothing in prod)
 *    rather than a toast.
 *  - `exportCsv` is synchronous despite the `async`, so `isExporting` flips
 *    back before React paints and the button's spinner never actually shows.
 *
 * Both predate this hook. Worth fixing, but as its own change.
 */
export function useIncidentsExport(params: Params) {
  const { productType, allIncidents, allScanIncidents } = params;
  const [isExporting, setIsExporting] = useState(false);
  const { exportCsv } = useExportCsv();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const dataToExport =
        productType === 'rg' ? allIncidents : allScanIncidents;

      if (!dataToExport?.length) {
        throw new Error('No data to export');
      }

      const formattedData =
        productType === 'rg'
          ? getFormattedExportData(allIncidents)
          : getScanFormattedExportData(allScanIncidents || []);

      if (!Array.isArray(formattedData) || formattedData.length === 0) {
        throw new Error('Formatted data is invalid or empty');
      }
      exportCsv({
        formattedData,
        fileName: 'hakimo-incident-reports-export.csv',
        fileType: 'csv',
      });
    } catch (error) {
      throw new Error(
        `Export Failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsExporting(false);
    }
  };

  return { isExporting, handleExport };
}

export default useIncidentsExport;
